import Toaster, { showToast } from "../Toaster/Toaster";
import CareHiresLogo from "../../assets/img/CH_New_Logo.png";
import { Card, Form, Input, Button, Typography } from "antd";
import "./SSO.css";
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  browserLocalPersistence,
  deleteUser,
  getAdditionalUserInfo,
  getAuth,
  OAuthProvider,
  signInWithPopup,
  UserCredential,
} from "firebase/auth";
import { auth } from "../../services/firebase";
import MicrosoftLogo from "../../assets/img/microsoft.png";
import { setCookie } from "../../utils/cookies";
import { getDomain } from "tldts";
import {
  getUserCompaniesByUID,
  getUserDataByUID,
  updateLastRequestTime,
  userSignOut,
  recordLastSignedIn,
} from "../../services/auth.service";
import { datadogRum } from "@datadog/browser-rum";
import { getUserImages } from "../../services/common.service";
import { isMobile } from "../../utils/device";
import { isAxiosError } from "axios";
import { getDeviceFingerprint } from "../../utils/fingerprint";
import type { AccountFlaggedError } from "../../types/security";

const { Title, Text, Link } = Typography;

const firebaseAuth = getAuth();

const SSO: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [requestId, setRequestId] = useState("");
  const navigate = useNavigate();
  const [isValidated, setIsValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoCompleted, setSsoCompleted] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [userCompanies, setUserCompanies] = useState<any>(null);
  const [expiresInDays, setExpiresInDays] = useState<number>(0);
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [showOrganizationSwitcher, setShowOrganizationSwitcher] =
    useState(false);

  const cookieName = "ch_token";

  const validateRequestId = async (request_id: string) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_AUTH_API_URL}/api/v1/auth/oauth/invitations/${request_id}`
      );
      return response.data;
    } catch (error: any) {
      showToast("Invalid request ID", { type: "error" });
      return;
    }
  };

  const updateOAuthInvitation = async (uid: string, accessToken: string) => {
    try {
      let headers: any = {
        Authorization: `Bearer ${accessToken}`,
      };
      const response = await axios.put(
        `${import.meta.env.VITE_AUTH_API_URL}/api/v1/auth/oauth/invitations/${requestId}`,
        { uid },
        { headers }
      );
      return response.data;
    } catch (error: any) {
      showToast("Invalid request ID", { type: "error" });
      return;
    }
  };

  const getDomainUrl = () => {
    return getDomain(window.location.href) || "";
  };

  useEffect(() => {
    const validateToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const request_id = urlParams.get("request_id")?.trim() || "";
      if (!request_id) {
        showToast("Invalid request ID", { type: "error" });
        return;
      } else {
        setRequestId(request_id);
      }

      if (!isValidated) {
        try {
          setLoading(true);
          const data = await validateRequestId(request_id);
          console.log(data.data);
          if (data) {
            setName(data.data.userInformation.fullName);
            setEmail(data.data.userInformation.email);
            setSsoCompleted(data.data.used);
            setIsValidated(true);
            setLoading(false);
          }
        } catch (error) {
          setIsValidated(false);
          showToast("Error validating request ID", { type: "error" });
        }
      }
    };

    validateToken();
  }, [isValidated]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrlParam = queryParams.get("redirectUrl");
    if (redirectUrlParam) {
      setRedirectUrl(redirectUrlParam);
      sessionStorage.setItem("redirectUrl", redirectUrlParam); // Store in session storage
    } else {
      const storedRedirectUrl = sessionStorage.getItem("redirectUrl");
      if (storedRedirectUrl) {
        setRedirectUrl(storedRedirectUrl);
      } else {
        // setRedirectUrl(`${import.meta.env.VITE_SCM_DASHBOARD_URL}`);
      }
    }
  }, []);

  const signUpWithMicrosoft = () => {
    setLoading(true);
    const provider = new OAuthProvider("microsoft.com");
    firebaseAuth
      .setPersistence(browserLocalPersistence)
      .then(() => {
        return signInWithPopup(auth, provider);
      })
      .then(async (response) => {
        const uid = response.user?.uid;
        if (uid) {
          if (email === response.user?.email) {
            completeSignUp(response);
          } else {
            const additionalUserInfo = getAdditionalUserInfo(response);
            if (additionalUserInfo?.isNewUser) {
              await deleteUser(response.user!);
            }
            showToast("Email does not match", { type: "error" });
            setLoading(false);
          }
        }
      })
      .catch((error) => {
        console.error("Error during Microsoft login:", error);
        showToast("Error during Microsoft login", { type: "error" });
        setLoading(false);
      });
  };

  const extractPageName = (url: string) => {
    try {
      const path = new URL(url).pathname;
      return path.split("/").pop();
    } catch (error) {
      console.error("Error extracting page name from URL:", error);
      return "";
    }
  };

  const handleWorkerOrMobileLogin = () => {
    if (isMobile()) {
      showToast("Id app doesn't support access from mobile devices.", {
        type: "error",
      });
      setLoading(false);
      setTimeout(() => {
        userSignOut();
      }, 3000);
      return true;
    }

    return false;
  };

  const completeSignUp = (userCredential: UserCredential) => {
    userCredential.user?.getIdTokenResult().then(async (idTokenResult) => {
      const token = idTokenResult.token;
      const expirationTime = new Date(idTokenResult.expirationTime);

      const expiresInDays =
        (expirationTime.getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24);
      setCookie("firebase_token", token, expiresInDays, getDomainUrl());
      setExpiresInDays(expiresInDays);
      try {
        const isWorker = idTokenResult.claims["worker"] == true;
        if(isWorker) {
          showToast("Access denied. Please contact admin for account setup.", { type: "error" });
        }
        const response = await updateLastRequestTime(token);
        
        if (response.success && response.data) {
          const user = userCredential.user?.toJSON() as {
            email: string | undefined;
            fullName: string | undefined;
            uid: string;
            stsTokenManager: { accessToken: string };
          };

          // Start recording user sessions
          datadogRum.setUser({
            id: user.uid,
            name: user.fullName,
            email: user.email,
          });
          datadogRum.startSessionReplayRecording();

          // Security check: device fingerprint + login telemetry before issuing ch_token
          const fingerprint = await getDeviceFingerprint();
          try {
            const securityResponse = await recordLastSignedIn(user.uid, token, fingerprint);
            if (securityResponse.data?.deviceBlocked) {
              userSignOut();
              showToast("Your device has been blocked. Please contact support.", { type: "error" });
              setLoading(false);
              return;
            }
            if (securityResponse.data?.passwordExpired && securityResponse.data?.passwordChangeToken) {
              userSignOut();
              window.location.href = `/set-password?token=${securityResponse.data.passwordChangeToken}`;
              return;
            }
            if (securityResponse.data?.isNewDevice) {
              showToast("A security verification email has been sent to your inbox.", { type: "warning" });
            }
          } catch (securityError: unknown) {
            if (
              isAxiosError(securityError) &&
              securityError.response?.status === 403 &&
              (securityError.response.data as AccountFlaggedError).accountFlagged
            ) {
              userSignOut();
              showToast("Your account has been flagged. Please contact support.", { type: "error" });
              setLoading(false);
              return;
            }
            userSignOut();
            showToast("Unable to complete sign-in. Please try again.", { type: "error" });
            setLoading(false);
            return;
          }

          if (user) {
            const fetchResponse = await fetch(
              `${import.meta.env.VITE_FIREBASE_API_URL}/auth/access_token?uid=${user.uid}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const data = await fetchResponse.json();

            if (data.success) {
              const accessToken = data.data;

              setCookie(cookieName, accessToken, expiresInDays, getDomainUrl());

              const updateResponse = await updateOAuthInvitation(
                user.uid,
                token
              );
              if (!updateResponse || !updateResponse.success) {
                showToast("Error updating OAuth invitation", { type: "error" });
                return;
              }
              // if (handleWorkerOrMobileLogin()) {
              //   return;
              // }

              const userData = await getUserDataByUID(user.uid, token);
              if (userData.data.userGroup === "Provider") {
                let companies: any = [];
                const userCompaniesResponse = await getUserCompaniesByUID(
                  userData.data?._id,
                  "provider",
                  userData.data?.provider?._id,
                  token
                );
                const subCompanies = userCompaniesResponse?.data;
                const providers = subCompanies
                  .filter((x: { provider: any }) => x.provider)
                  .map(
                    (company: {
                      provider: { id: any; companyName: any };
                      careHomes: any;
                      isAgreedToAgencyAndProviderTermsAndConditions: any;
                      isAgreedToCareHiresTermsAndConditions: any;
                      isAuthorizer: any;
                      isDefault: any;
                      isPrimaryUser: any;
                      jobTitle: any;
                      phone: any;
                      userType: any;
                      apps: any;
                    }) => ({
                      id: company.provider.id,
                      name: company.provider.companyName,
                      careHomes: company.careHomes,
                      isAgreedToAgencyAndProviderTermsAndConditions:
                        company.isAgreedToAgencyAndProviderTermsAndConditions,
                      isAgreedToCareHiresTermsAndConditions:
                        company.isAgreedToCareHiresTermsAndConditions,
                      isAuthorizer: company.isAuthorizer,
                      isDefault: company.isDefault,
                      isPrimaryUser: company.isPrimaryUser,
                      jobTitle: company.jobTitle,
                      phone: company.phone,
                      provider: company.provider,
                      userType: company.userType,
                      isProvider: true,
                      apps: company.apps,
                    })
                  );
                if (providers.length > 0) {
                  const res = await getUserImages(
                    "provider",
                    providers.map((x: { id: any }) => x.id),
                    token
                  );
                  if (res.data.length > 0) {
                    for (const img of res.data) {
                      const provider = providers.find(
                        (x: { id: any }) => x.id === img.id
                      );
                      if (provider) {
                        provider.image = img.image;
                      }
                    }
                  }

                  companies = [...providers];
                  companies.forEach((c: any) => {
                    if (c.provider?._id === userData.data.provider?._id) {
                      c.isSelected = true;
                    } else {
                      c.isSelected = false;
                    }
                  });
                }
                localStorage.setItem("companies", JSON.stringify(companies));
                localStorage.setItem(
                  "ch_user_data",
                  JSON.stringify(userData.data)
                );
                setCookie(
                  "ch_user_data",
                  JSON.stringify(userData.data),
                  expiresInDays,
                  getDomainUrl()
                );

                //TODO: Redirect to the page where user initiated SSO login
                //Temporarily redirecting to SCM Dashboard
                if (import.meta.env.VITE_SCM_DASHBOARD_URL) {
                  window.location.href =
                    import.meta.env.VITE_SCM_DASHBOARD_URL || "";
                } else {
                  showToast(
                    "SSO setup completed successfully. Please navigate to the dashboard!",
                    { type: "success" }
                  );
                }
              } else if (userData.data.userGroup === "Agency") {
                let companies: any = [];
                const userCompaniesResponse = await getUserCompaniesByUID(
                  userData.data?._id,
                  "agency",
                  userData.data?.agency?._id,
                  token
                );
                const subCompanies = userCompaniesResponse?.data;
                const agencies = subCompanies
                  .filter((x: { agency: any }) => x.agency)
                  .map(
                    (company: {
                      agency: { id: any; businessName: any };
                      locations: any;
                      isAgreedToAgencyAndProviderTermsAndConditions: any;
                      isAgreedToCareHiresTermsAndConditions: any;
                      isAuthorizer: any;
                      isDefault: any;
                      isPrimaryUser: any;
                      jobTitle: any;
                      phone: any;
                      userType: any;
                      apps: any;
                    }) => ({
                      id: company.agency.id,
                      name: company.agency.businessName,
                      locations: company.locations,
                      isAgreedToAgencyAndProviderTermsAndConditions:
                        company.isAgreedToAgencyAndProviderTermsAndConditions,
                      isAgreedToCareHiresTermsAndConditions:
                        company.isAgreedToCareHiresTermsAndConditions,
                      isAuthorizer: company.isAuthorizer,
                      isDefault: company.isDefault,
                      isPrimaryUser: company.isPrimaryUser,
                      jobTitle: company.jobTitle,
                      phone: company.phone,
                      agency: company.agency,
                      userType: company.userType,
                      isAgency: true,
                      apps: company.apps,
                    })
                  );
                if (agencies.length > 0) {
                  const res = await getUserImages(
                    "agency",
                    agencies.map((x: { id: any }) => x.id),
                    token
                  );
                  if (res.data.length > 0) {
                    for (const img of res.data) {
                      const agency = agencies.find(
                        (x: { id: any }) => x.id === img.id
                      );
                      if (agency) {
                        agency.image = img.image;
                      }
                    }
                  }
                  companies = [...agencies];
                  companies.forEach((c: any) => {
                    if (c.agency?._id === userData.data.agency?._id) {
                      c.isSelected = true;
                    } else {
                      c.isSelected = false;
                    }
                  });
                }
                localStorage.setItem("companies", JSON.stringify(companies));
                localStorage.setItem(
                  "ch_user_data",
                  JSON.stringify(userData.data)
                );
                setCookie(
                  "ch_user_data",
                  JSON.stringify(userData.data),
                  expiresInDays,
                  getDomainUrl()
                );

                //TODO: Redirect to the page where user initiated SSO login
                //Temporarily redirecting to SCM Dashboard
                if (import.meta.env.VITE_SCM_DASHBOARD_URL) {
                  window.location.href =
                    import.meta.env.VITE_SCM_DASHBOARD_URL || "";
                } else {
                  showToast(
                    "SSO setup completed successfully. Please navigate to the dashboard!",
                    { type: "success" }
                  );
                }
              } else if (userData.data.userGroup === "Admin") {
                localStorage.setItem(
                  "ch_user_data",
                  JSON.stringify(userData.data)
                );
                setCookie(
                  "ch_user_data",
                  JSON.stringify(userData.data),
                  expiresInDays,
                  getDomainUrl()
                );

                //TODO: Redirect to the page where user initiated SSO login
                //Temporarily redirecting to SCM Dashboard
                if (import.meta.env.VITE_SCM_DASHBOARD_URL) {
                  window.location.href =
                    import.meta.env.VITE_SCM_DASHBOARD_URL || "";
                } else {
                  navigate("/dashboard");
                }
              }
            } else {
              showToast("Failed to get access token", { type: "error" });
              setLoading(false);
            }
          }
        }
      } catch (error) {
        console.error("Error updating last request time:", error);
      }
    });
  };

  return (
    <div className="forgot-password-container">
      <Toaster position="top-right" />
      <img
        className="forgot-password-logo"
        src={CareHiresLogo}
        alt="Care Hires Logo"
      />
      {/* <Card bordered={false} className="forgot-password-card">
        <Title level={2} className="forgot-password-subtitle">
          Complete Single Sign-On (SSO) Setup
        </Title>
        <Text className="forgot-password-text">
          {name ? `Hello, ${name}` : "Hello,"} please click the below button to
          complete the SSO setup.
        </Text>
        <Button onClick={signUpWithMicrosoft} className="microsoft-login">
          <img src={MicrosoftLogo} width="24" alt="Microsoft logo" />
          Sign up with Microsoft
        </Button>
      </Card> */}
      <div className="container">
        <div className="icon-wrapper">
          <svg viewBox="0 0 24 24">
            <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>

        <h2>
          {ssoCompleted ? "SSO Already Configured" : "Complete SSO Setup"}
        </h2>
        <p className="subtitle">
          Secure your account with Single Sign-On authentication
        </p>

        <div className="greeting">
          {ssoCompleted ? (
            <>
              Hello, <strong>{name ? `${name}` : ""}</strong>! Your account is
              already secured with Microsoft SSO. No further action needed.
            </>
          ) : (
            <>
              {" "}
              Hello, <strong>{name ? `${name}` : ""}</strong>! Please complete
              your SSO setup to continue.
            </>
          )}
        </div>

        <button
          className="sso-button"
          disabled={loading || ssoCompleted}
          onClick={signUpWithMicrosoft}
        >
          <svg width="20" height="20" viewBox="0 0 21 21">
            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
          </svg>
          Sign in with Microsoft
        </button>

        <p className="help-text">
          Need help?{" "}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://ask.carehires.com/en/knowledge/kb-tickets/new"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default SSO;
